import {
  IMEI_TIMEOUT_MS,
  INSTALL_CHUNK_SIZE_BYTES,
  INSTALL_COMMIT_POLL_INTERVAL_MS,
  INSTALL_COMMIT_TIMEOUT_MS,
  INSTALL_MAX_ATTEMPTS,
  INSTALL_PROGRESS_INTERVAL_MS,
  INSTALL_RETRY_DELAY_MS,
  INSTALL_STALL_CHECK_INTERVAL_MS,
  INSTALL_STALL_TIMEOUT_MS,
  MOS_IDS_BROADCAST_ACTION,
  MOS_IDS_BROADCAST_RECEIVER,
  POLL_INTERVAL_MS,
  POWER_OFF_TIMEOUT_MS,
  STEP_MAX_ATTEMPTS,
  STEP_RETRY_DELAY_MS,
} from '@mxa-argos/modules/forja/constants/forja.constants';
import type { ForjaApk } from '@mxa-argos/modules/forja/interfaces/forja-apk.interface';
import type { ForjaInstallState } from '@mxa-argos/modules/forja/interfaces/forja-install-progress.interface';
import type {
  ForjaDeviceEvent,
  ForjaProgressEvent,
} from '@mxa-argos/modules/forja/interfaces/forja-progress-event.interface';
import type { ForjaStep } from '@mxa-argos/modules/forja/interfaces/forja-step.interface';
import type { ForjaStepResult } from '@mxa-argos/modules/forja/interfaces/forja-step-result.interface';
import type { ForjaWaitReason } from '@mxa-argos/modules/forja/interfaces/forja-wait.interface';
import type { ForjaEngineEvents } from '@mxa-argos/modules/forja/lib/engine/forja-engine-events';
import {
  ForjaError,
  toForjaErrorCode,
  toForjaErrorDetail,
} from '@mxa-argos/modules/forja/lib/forja-error';
import { sleep } from '@mxa-argos/modules/forja/lib/sleep';
import { adbShell } from '@mxa-argos/modules/forja/lib/transport/adb-shell';
import type { Adb } from '@yume-chan/adb';
import { PackageManager } from '@yume-chan/android-bin';
import { type MaybeConsumable, ReadableStream } from '@yume-chan/stream-extra';

const IMEI_PATTERN = /^\d{15}$/;
const QUOTED_CHUNK_PATTERN = /'([^']*)'/g;

export class ForjaDeviceSession {
  private currentIMEI: string | null = null;
  private readonly disconnection: Promise<never>;

  constructor(
    private readonly adb: Adb,
    readonly serial: string,
    private readonly events: ForjaEngineEvents,
  ) {
    this.disconnection = adb.disconnected.then(() => {
      throw new ForjaError('DEVICE_DISCONNECTED', `${serial} left the USB bus`);
    });
    // Whichever races lose to a command keep no handler for this rejection.
    void this.disconnection.catch(() => undefined);
  }

  get imei(): string {
    if (!this.currentIMEI) {
      throw new ForjaError('IMEI_UNREADABLE', `IMEI never read for ${this.serial}`);
    }

    return this.currentIMEI;
  }

  emit(event: ForjaDeviceEvent): void {
    this.events.emit({ ...event, serial: this.serial } as ForjaProgressEvent);
  }

  async shell(...args: string[]): Promise<string> {
    const startedAtMillis = Date.now();

    try {
      // A command issued over a dead transport never settles, so without this race the run hangs
      // on it forever instead of failing.
      const output = await Promise.race([adbShell(this.adb, ...args), this.disconnection]);
      this.events.emitShellLog({
        serial: this.serial,
        command: args.join(' '),
        output,
        durationMs: Date.now() - startedAtMillis,
      });
      return output;
    } catch (error) {
      this.events.emitShellLog({
        serial: this.serial,
        command: args.join(' '),
        output: '',
        durationMs: Date.now() - startedAtMillis,
        error: String(error),
      });
      throw error;
    }
  }

  async runStep(step: ForjaStep): Promise<ForjaStepResult> {
    const maxAttempts = step.maxAttempts ?? STEP_MAX_ATTEMPTS;
    const timeoutDetail = `applied, but the check never passed within ${step.verifyTimeoutMs}ms`;
    let hasApplied = false;
    let failure: ForjaStepResult = {
      name: step.name,
      status: 'FAILED',
      errorCode: 'STEP_TIMEOUT',
      errorDetail: timeoutDetail,
    };

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      if (attempt > 1) {
        this.emit({ type: 'step-retry', name: step.name, attempt, maxAttempts });
        await sleep(STEP_RETRY_DELAY_MS);
      }

      try {
        if (await step.verify(this)) {
          return { name: step.name, status: hasApplied ? 'APPLIED' : 'OK' };
        }

        await step.apply(this);
        hasApplied = true;

        const deadline = Date.now() + step.verifyTimeoutMs;
        while (Date.now() < deadline) {
          if (await step.verify(this)) {
            return { name: step.name, status: 'APPLIED' };
          }
          await sleep(1000);
        }

        failure = {
          name: step.name,
          status: 'FAILED',
          errorCode: 'STEP_TIMEOUT',
          errorDetail: timeoutDetail,
        };
      } catch (error) {
        failure = {
          name: step.name,
          status: 'FAILED',
          errorCode: toForjaErrorCode(error),
          errorDetail: toForjaErrorDetail(error),
        };
      }
    }

    return failure;
  }

  async runPhase(phase: string, steps: ForjaStep[]): Promise<ForjaStepResult[]> {
    this.emit({ type: 'phase-start', phase });

    const results: ForjaStepResult[] = [];

    for (const step of steps) {
      this.emit({ type: 'step-start', name: step.name });
      const result = await this.runStep(step);
      this.emit({ type: 'step-result', result });
      results.push(result);
    }

    return results;
  }

  async waitFor(
    reason: ForjaWaitReason,
    condition: () => Promise<boolean>,
    timeoutMs: number,
  ): Promise<boolean> {
    this.emit({ type: 'wait-start', reason, untilEpochMillis: Date.now() + timeoutMs });

    try {
      const deadline = Date.now() + timeoutMs;

      while (Date.now() < deadline) {
        if (await this.checkWaitCondition(condition)) {
          return true;
        }
        await sleep(POLL_INTERVAL_MS);
      }

      return false;
    } finally {
      this.emit({ type: 'wait-end' });
    }
  }

  // A read that fails mid-wait (a wedged dumpsys, a command lost to a reconnection) is not an
  // answer, so the wait keeps polling; losing the device is, and it ends the run.
  private async checkWaitCondition(condition: () => Promise<boolean>): Promise<boolean> {
    try {
      return await condition();
    } catch (error: unknown) {
      if (error instanceof ForjaError && error.code === 'DEVICE_DISCONNECTED') {
        throw error;
      }

      console.warn(`${this.serial}: wait condition failed`, error);
      return false;
    }
  }

  async installApk(apk: ForjaApk): Promise<void> {
    const packageName = apk.release.packageName;

    for (let attempt = 1; attempt <= INSTALL_MAX_ATTEMPTS; attempt += 1) {
      try {
        await this.transferApk(apk);

        if (attempt > 1) {
          console.info(`${this.serial}: ${packageName} installed on attempt ${attempt}`);
        }

        return;
      } catch (error) {
        if (attempt === INSTALL_MAX_ATTEMPTS) {
          throw error;
        }

        console.warn(`${this.serial}: ${packageName} attempt ${attempt} failed`, error);
        await sleep(INSTALL_RETRY_DELAY_MS);
      }
    }
  }

  private async transferApk(apk: ForjaApk): Promise<void> {
    const packageName = apk.release.packageName;
    const totalBytes = apk.blob.size;
    const startedAt = Date.now();
    const reader = apk.blob.stream().getReader();

    let transferredBytes = 0;
    let lastChunkAt = startedAt;
    let lastEmitAt = 0;
    let uploadFinishedAt: number | null = null;
    let hasSettled = false;
    let stallTimer: ReturnType<typeof setInterval> | undefined;

    const emitProgress = (state: ForjaInstallState): void => {
      const elapsedMs = Date.now() - startedAt;

      this.emit({
        type: 'install-progress',
        packageName,
        state,
        transferredBytes,
        totalBytes,
        bytesPerSecond: elapsedMs > 0 ? Math.round((transferredBytes * 1000) / elapsedMs) : 0,
      });
    };

    const source = new ReadableStream<MaybeConsumable<Uint8Array>>({
      pull: async (controller) => {
        const chunk = await readChunk(reader, INSTALL_CHUNK_SIZE_BYTES);

        if (!chunk) {
          uploadFinishedAt = Date.now();
          emitProgress('installing');
          controller.close();
          return;
        }

        transferredBytes += chunk.length;
        lastChunkAt = Date.now();

        if (lastChunkAt - lastEmitAt >= INSTALL_PROGRESS_INTERVAL_MS) {
          lastEmitAt = lastChunkAt;
          emitProgress('transferring');
        }

        controller.enqueue(chunk);
      },
      cancel: () => {
        void reader.cancel();
      },
    });

    const install = new PackageManager(this.adb).installStream(totalBytes, source);
    // The watchdog can win the race below, leaving this rejection without a handler.
    void install.catch(() => undefined);

    const stalled = new Promise<never>((_, reject) => {
      stallTimer = setInterval(() => {
        if (uploadFinishedAt === null) {
          if (Date.now() - lastChunkAt > INSTALL_STALL_TIMEOUT_MS) {
            reject(
              new ForjaError(
                'INSTALL_STALLED',
                `${packageName}: no bytes moved in ${INSTALL_STALL_TIMEOUT_MS}ms at ${transferredBytes}/${totalBytes}`,
              ),
            );
          }
          return;
        }

        if (Date.now() - uploadFinishedAt > INSTALL_COMMIT_TIMEOUT_MS) {
          reject(
            new ForjaError(
              'INSTALL_STALLED',
              `${packageName}: fully uploaded but pm did not finish in ${INSTALL_COMMIT_TIMEOUT_MS}ms`,
            ),
          );
        }
      }, INSTALL_STALL_CHECK_INTERVAL_MS);
    });

    // `pm` can finish committing while its exec stream stays open, so the package showing up is a
    // more reliable completion signal than the install promise settling.
    const pollUntilInstalled = async (): Promise<void> => {
      while (!hasSettled) {
        await sleep(INSTALL_COMMIT_POLL_INTERVAL_MS);

        if (uploadFinishedAt === null) {
          continue;
        }

        if (await this.isPackageInstalled(packageName).catch(() => false)) {
          return;
        }
      }
    };

    try {
      await Promise.race([install, stalled, pollUntilInstalled()]);
    } finally {
      hasSettled = true;
      clearInterval(stallTimer);
      // Not awaited: a dead pipe must not turn the cleanup itself into the next stall.
      void reader.cancel().catch(() => undefined);
    }
  }

  async powerOff(): Promise<boolean> {
    // `reboot -p` tears the socket down along with the device, so waiting for the command to
    // return hangs or throws on a healthy shutdown. The disconnection is the only real signal.
    void this.adb.power.powerOff().catch((error: unknown) => {
      console.warn(`${this.serial}: power off command failed`, error);
    });

    return this.waitForDisconnection();
  }

  async reboot(): Promise<boolean> {
    // Same teardown caveat as powerOff: the `reboot:` service kills the socket on success.
    void this.adb.power.reboot().catch((error: unknown) => {
      console.warn(`${this.serial}: reboot command failed`, error);
    });

    return this.waitForDisconnection();
  }

  private waitForDisconnection(): Promise<boolean> {
    return Promise.race([
      this.adb.disconnected.then(() => true),
      sleep(POWER_OFF_TIMEOUT_MS).then(() => false),
    ]);
  }

  async isPackageInstalled(packageName: string): Promise<boolean> {
    const output = await this.shell('pm', 'list', 'packages', packageName);
    return output.split('\n').some((line) => line.trim() === `package:${packageName}`);
  }

  async hasManagedConfiguration(): Promise<boolean> {
    const output = await this.shell(
      'am',
      'broadcast',
      '-a',
      MOS_IDS_BROADCAST_ACTION,
      '-n',
      MOS_IDS_BROADCAST_RECEIVER,
    );
    const payload = /data="(.*)"\s*$/m.exec(output)?.[1];

    if (!payload) {
      return false;
    }

    try {
      const ids = JSON.parse(payload) as { imei?: string | null };
      return ids.imei === this.imei;
    } catch {
      return false;
    }
  }

  // These dumps are tens of kilobytes each and the waits below poll them every few seconds on
  // every device at once, so the filtering happens on the phone and only the matching lines cross
  // the USB pipe.
  async isKioskEngaged(): Promise<boolean> {
    const state = await this.shell('dumpsys activity activities | grep mLockTaskModeState');
    return state.includes('mLockTaskModeState=LOCKED');
  }

  async readForegroundWindow(): Promise<string> {
    const line = await this.shell('dumpsys window | grep -m1 mCurrentFocus');

    return line.trim() || '<unknown>';
  }

  // mFocusedApp (activity focus) instead of mCurrentFocus (window focus): system overlays like
  // the notification shade or the sticky-immersive confirmation steal window focus while the app
  // stays in the foreground.
  async isAppInForeground(packageName: string): Promise<boolean> {
    const line = await this.shell('dumpsys window | grep -m1 mFocusedApp');

    return line.includes(packageName);
  }

  async collapseNotificationShade(): Promise<void> {
    await this.shell('cmd', 'statusbar', 'collapse');
  }

  // The iphonesubinfo transaction code is not stable across Android builds, hence the vendor prop
  // fallbacks below.
  async readIMEI(): Promise<string> {
    const attempts: string[] = [];

    const strategies: [string, () => Promise<string>][] = [
      [
        'service call iphonesubinfo 1',
        () => this.shell('service', 'call', 'iphonesubinfo', '1', 's16', 'com.android.shell'),
      ],
      [
        'service call iphonesubinfo 3',
        () => this.shell('service', 'call', 'iphonesubinfo', '3', 's16', 'com.android.shell'),
      ],
      ['getprop ro.ril.oem.imei', () => this.shell('getprop', 'ro.ril.oem.imei')],
      ['getprop persist.radio.imei', () => this.shell('getprop', 'persist.radio.imei')],
    ];

    for (const [name, run] of strategies) {
      const output = await Promise.race([
        run(),
        sleep(IMEI_TIMEOUT_MS).then(() => '<timed out>'),
      ]).catch((error: unknown) => `<failed: ${String(error)}>`);
      const imei = parseIMEI(output);

      if (imei) {
        this.currentIMEI = imei;
        return imei;
      }

      attempts.push(`${name} -> ${output.replace(/\s+/g, ' ').trim().slice(0, 160)}`);
    }

    throw new ForjaError('IMEI_UNREADABLE', `Attempts: ${attempts.join(' | ')}`);
  }
}

// `service call` renders its UTF-16 payload as quoted chunks, with non-printable bytes as dots.
function parseIMEI(output: string): string | null {
  const fromParcel = [...output.matchAll(QUOTED_CHUNK_PATTERN)]
    .map((match) => match[1])
    .join('')
    .replace(/[.\s]/g, '');

  if (IMEI_PATTERN.test(fromParcel)) {
    return fromParcel;
  }

  const bare = output.replace(/\s/g, '');

  return IMEI_PATTERN.test(bare) ? bare : null;
}

async function readChunk(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  size: number,
): Promise<Uint8Array | null> {
  const parts: Uint8Array[] = [];
  let length = 0;

  while (length < size) {
    const { done, value } = await reader.read();

    if (done) {
      break;
    }

    parts.push(value);
    length += value.length;
  }

  if (length === 0) {
    return null;
  }

  if (parts.length === 1) {
    return parts[0];
  }

  const chunk = new Uint8Array(length);
  let offset = 0;

  for (const part of parts) {
    chunk.set(part, offset);
    offset += part.length;
  }

  return chunk;
}
